import React from "react";
import { Nav, NavItem, NavLink } from "reactstrap";
import classnames from "classnames";

/**
 * Responsive horizontal tab bar. Wraps Reactstrap's Nav/NavItem/NavLink
 * with consistent styling (custom-nav-tabs primary), icon support, and
 * an active-count badge — same visual language as the Display Boards
 * page tab strip and the Mafia tile groups.
 *
 * Pure presentation. The caller owns the activeKey state and the
 * onChange handler; this component does NOT lazy-render the panel
 * content. Use it together with a TabContent/TabPane block, or simply
 * `{activeKey === "x" && <Foo />}` — that pattern preserves the existing
 * "render only the active tab" behavior used across the dashboard.
 *
 * Props
 *  - tabs:        array of { key, label, icon?, count?, badge?, disabled?, hidden? }
 *                 `badge` is an optional React node that, when present,
 *                 OVERRIDES the auto-rendered count badge. Use it for
 *                 custom-rendered indicators (e.g. completion progress
 *                 like "3/4" or a green check), where a plain numeric
 *                 count isn't enough.
 *  - activeKey:   currently active tab key
 *  - onChange:    (key) => void
 *  - variant:     "underline" (default) | "pills"
 *  - color:       active color (default "primary")
 *  - justified:   bool — equally space the tabs across the row
 *  - className:   extra className on the Nav wrapper
 *  - size:        "sm" | "md" (default) — controls horizontal padding
 */
const ResponsiveTabs = ({
  tabs = [],
  activeKey,
  onChange,
  variant = "underline",
  color = "primary",
  justified = false,
  className = "",
  size = "md",
}) => {
  const visible = tabs.filter((t) => !t.hidden);

  const navClass = classnames(
    {
      "nav-tabs": variant === "underline",
      "nav-pills": variant === "pills",
      "nav-tabs-custom": variant === "underline",
      [`nav-${color}`]: true,
      "nav-justified": justified,
    },
    "mb-3",
    "flex-wrap",
    className
  );

  const padX = size === "sm" ? "0.5rem" : "1rem";

  return (
    <Nav
      tabs={variant === "underline"}
      pills={variant === "pills"}
      className={navClass}
      role="tablist"
    >
      {visible.map((tab) => {
        const isActive = tab.key === activeKey;
        const isDisabled = !!tab.disabled;
        return (
          <NavItem key={tab.key}>
            <NavLink
              role="tab"
              aria-selected={isActive}
              aria-disabled={isDisabled}
              className={classnames({
                active: isActive,
                disabled: isDisabled,
              })}
              style={{
                cursor: isDisabled ? "not-allowed" : "pointer",
                padding: `0.75rem ${padX}`,
                whiteSpace: "nowrap",
                opacity: isDisabled ? 0.5 : 1,
              }}
              onClick={() => {
                if (isDisabled) return;
                if (typeof onChange === "function" && tab.key !== activeKey) {
                  onChange(tab.key);
                }
              }}
            >
              {tab.icon ? (
                <i
                  className={`${tab.icon} d-none d-sm-inline-block me-1 align-bottom`}
                  aria-hidden="true"
                />
              ) : null}
              <span className="align-middle">{tab.label}</span>
              {tab.badge !== undefined && tab.badge !== null ? (
                <span className="ms-1 align-middle">{tab.badge}</span>
              ) : typeof tab.count === "number" ? (
                <span
                  className={`badge ms-1 ${
                    isActive
                      ? `bg-${color}-subtle text-${color}`
                      : "bg-light text-muted"
                  }`}
                  style={{ fontSize: 11, fontWeight: 500 }}
                >
                  {tab.count}
                </span>
              ) : null}
            </NavLink>
          </NavItem>
        );
      })}
    </Nav>
  );
};

export default ResponsiveTabs;
